<template>
  <TheMenuLayout>
    <BaseSectionHeader class="mb-6">Planning</BaseSectionHeader>
    <div class="d-flex flex-row justify-space-between mb-4 pr-4 pl-4">
      <BaseButton
        color="primary"
        @click="goToPreviousView"
      >
        {{ $t(previousBtn) }}
      </BaseButton>
      <BaseButton
        color="primary"
        @click="updateView"
      >
        {{ $t(viewBtn) }}
      </BaseButton>
      <BaseButton
        color="primary"
        @click="goToNextView"
      >
        {{ $t(nextBtn) }}
      </BaseButton>
    </div>
    <div class="planning-top-month primary--text">
      {{ displayedMonth }}
    </div>
    <div class="planning-container">
      <div
        id="calendar"
        ref="calendar"
      />
    </div>
    <BaseButton
        color="secondary"
        style="margin-top: 20px;"
        @click="downloadPlanning"
        :disabled="this.options.view === 'month'"
      >
      {{ $t('download planning pdf') }}
    </BaseButton>
  </TheMenuLayout>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { map } from 'lodash';
import '@toast-ui/calendar/dist/toastui-calendar.min.css';
import Calendar from '@toast-ui/calendar';

import BaseButton from '@/components/BaseButton.vue';
import BaseSectionHeader from '@/components/BaseSectionHeader.vue';
import TheMenuLayout from '@/components/TheMenuLayout.vue';

const DEFAULT_CALENDAR_OPTIONS = {
  startDayOfWeek: 1,
  dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
  narrowWeekend: true,
};

const WEEK_VIEW = 'Week view';
const MONTH_VIEW = 'Month view';
const PREVIOUS_WEEK = 'previous week';
const NEXT_WEEK = 'next week';
const PREVIOUS_MONTH = 'previous month';
const NEXT_MONTH = 'next month';

export default {
  name: 'Planning',
  components: {
    TheMenuLayout,
    BaseSectionHeader,
    BaseButton,
  },
  mounted() {
    this.fetchCourses().then(() => {
      const calendarContainer = this.$refs.calendar;
      this.calendar = new Calendar(calendarContainer, this.options);
      this.calendar.createEvents(this.events);

      this.changeEnglishToFrench();
    });
  },
  data() {
    return {
      calendar: null,
      viewBtn: MONTH_VIEW,
      previousBtn: PREVIOUS_WEEK,
      nextBtn: NEXT_WEEK,
      options: {
        view: 'week',
        useDetailPopup: true,
        week: {
          ...DEFAULT_CALENDAR_OPTIONS,
          taskView: false,
          showNowIndicator: false,
          showTimezoneCollapseButton: false,
          timezonesCollapsed: false,
          hourStart: 7,
          hourEnd: 22,
          eventView: ['time', 'allday'],
          collapseDuplicateEvents: false,
        },
        month: {
          ...DEFAULT_CALENDAR_OPTIONS,
          visibleWeeksCount: 0,
          workweek: false,
          isAlways6Weeks: false,
          visibleEventCount: 3,
        },
        template: {
          time: this.formatEventTemplate,
          popupDetailDate: this.popupDetailDate,
          popupDetailState: this.popupDetailState,
        },
        calendars: [{ id: 'cal1', name: 'Personal' }],
        theme: {
          week: {
            weekend: { backgroundColor: 'rgba(144, 162, 176, 0.05)' },
            timegridOneHour: { height: '50px' },
          },
          month: {
            weekend: { backgroundColor: 'rgba(144, 162, 176, 0.05)' },
          },
        },
      },
      displayedMonth: new Date().toLocaleDateString('fr-FR', { month: 'long' }).toUpperCase(),
    };
  },
  methods: {
    ...mapActions(['fetchCourses', 'downloadPdfPlanning']),
    formatEventTemplate(event) {
      const startTime = new Date(event.start);
      const formattedTime = `${startTime.getHours()}:${String(startTime.getMinutes()).padStart(2, '0')}`;
      return `
        <div>
        <b>${formattedTime}</b> ${event.title}<br>
        <p>${event.attendees}</p>
      </div>`;
    },
    popupDetailDate({ start, end }) {
      if (this.formatDate(start.d) === this.formatDate(end.d)) {
        return `${this.formatDateTime(start.d)} - ${this.formatTime(end.d)}`;
      }
      return `${this.formatDateTime(start.d)} - ${this.formatDateTime(end.d)}`;
    },
    popupDetailState() {
      return '';
    },
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    formatTime(date) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    formatDateTime(date) {
      return `${this.formatDate(date)} ${this.formatTime(date)}`;
    },
    updateView() {
      if (this.options.view === 'week') {
        this.calendar.changeView('month');
        this.options.view = 'month';
        this.viewBtn = WEEK_VIEW;
        this.previousBtn = PREVIOUS_MONTH;
        this.nextBtn = NEXT_MONTH;
      } else {
        this.calendar.changeView('week');
        this.options.view = 'week';
        this.viewBtn = MONTH_VIEW;
        this.previousBtn = PREVIOUS_WEEK;
        this.nextBtn = NEXT_WEEK;
        this.changeEnglishToFrench();
      }
      this.changeDisplayedMonth();
    },
    goToPreviousView() {
      this.calendar.prev();
      this.changeEnglishToFrench();
      this.changeDisplayedMonth();
    },
    goToNextView() {
      this.calendar.next();
      this.changeEnglishToFrench();
      this.changeDisplayedMonth();
    },
    changeDisplayedMonth() {
      this.displayedMonth = this.calendar.getDate().d.d.toLocaleDateString('fr-FR', { month: 'long' }).toUpperCase();
    },
    downloadPlanning() {
      let date = this.calendar.getDate();
      if (typeof date === 'string') {
        date = +date;
      }
      date = new Date(date);
      this.axios({
        url: `/courses/download_planning?date=${date}`,
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `planning-${date}.pdf`);
        document.body.appendChild(link);
        link.click();
      });
    },
    changeEnglishToFrench() {
      setTimeout(() => {
        const allDay = document.querySelector('span.toastui-calendar-left-content.toastui-calendar-template-alldayTitle');
        if (allDay) allDay.innerText = 'Journée entière';
        const times = document.querySelectorAll('.toastui-calendar-template-timegridDisplayPrimaryTime');
        times.forEach((timeElement) => {
          const time = timeElement.innerText.trim();
          if (time.endsWith('am') || time.endsWith('pm')) {
            let hour = parseInt(time.split(':')[0], 10);
            if (time.endsWith('pm') && hour !== 12) hour += 12;
            if (time.endsWith('am') && hour === 12) hour = 0;
            timeElement.innerText = `${hour}:00`;
          }
        });
      }, 50);
    },
  },
  computed: {
    ...mapState(['courses']),
    events() {
      return map(this.courses, (course) => ({
        id: course.id,
        title: course.subject_name,
        attendees: [course.admin_users_names],
        body: course.information,
        category: course.all_day ? 'allday' : 'time',
        start: course.start_date,
        end: course.end_date,
        backgroundColor: course.subject_color,
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
.planning-container {
  border: 1px solid grey;
}

#calendar {
  height: 500px;
}

.planning-top-month {
  text-align: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border: 1px solid grey;
  line-height: 30px;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 1px;
}

:deep(.toastui-calendar-panel.toastui-calendar-time) {
  height: 100%;
}

:deep(.toastui-calendar-timegrid) {
  height: 80vh;
  min-height: 300px;
}

:deep(.toastui-calendar-popup-section.toastui-calendar-section-header) {
  padding: 15px 17px;
  background-color: #0066CC;
  color: white;
  border-radius: 4px 4px 0px 0px;
}

:deep(.toastui-calendar-detail-container) {
  padding: 0px;
  border-radius: 4px 4px 4px 4px;
}

:deep(.toastui-calendar-popup-section.toastui-calendar-dropdown-section.toastui-calendar-state-section,
  .toastui-calendar-popup-section-item.toastui-calendar-popup-section-private.toastui-calendar-popup-button) {
  display: none;
}

:deep(.toastui-calendar-popup-section-item.toastui-calendar-popup-section-title) {
  width: 100%;
}

:deep(.toastui-calendar-popup-container) {
  font-weight: 400 !important;
  margin-bottom: 20px;
  position: fixed;
}

:deep(.toastui-calendar-popup-section.toastui-calendar-section-detail) {
  padding-left: 17px;
  margin: 0px;
}

:deep(.toastui-calendar-icon.toastui-calendar-ic-state-b) {
  display: none;
}

:deep(.toastui-calendar-detail-container > .toastui-calendar-popup-section.toastui-calendar-section-button) {
  display: none;
}

:deep(.toastui-calendar-detail-item.toastui-calendar-detail-item-indent) {
  margin-top: 20px;
}
</style>
